import { useMemo, useState } from "react";
import {
  Autocomplete,
  CircularProgress,
  TextField,
  type TextFieldProps,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import type { GetProjectsByIdsResponse, ProjectSummary } from "@aeriosiq/types";

/**
 * Theme-A polish (2026-05-08) — searchable async multi-select filter
 * for projects.
 *
 * The dropdown is sourced from `GET /projects?q=…` (dev-api: returns
 * up to 50 alphabetical matches; BE will mirror with the same shape).
 * Selections are kept upstream as `string[]` of project UUIDs and
 * become `?project_id=…&project_id=…` on the URL.
 *
 * For the chip-render path, the value is the bare UUID — we re-fetch
 * via `?ids=…` to get the names so the chips don't show raw UUIDs
 * after a deep-link land.
 */
export interface ProjectFilterProps {
  value: string[];
  onChange: (next: string[]) => void;
}

export function ProjectFilter({ value, onChange }: ProjectFilterProps) {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  // Searchable list — only fetched while the dropdown is open so the
  // toolbar doesn't fire a request on every page mount. Always send `q`
  // (even empty) because the real BE requires exactly one of `q`/`ids`
  // and treats `q=""` as "match all, top-50 recent" per
  // contracts/lyse/v1/openapi.yaml §/projects.
  const searchQuery = useQuery<GetProjectsByIdsResponse>({
    queryKey: ["projects", "search", search],
    enabled: open,
    queryFn: async () => {
      const response = await api.get<GetProjectsByIdsResponse>("/projects", {
        params: { q: search },
      });
      return response.data;
    },
    staleTime: 30_000,
  });

  // Resolve the currently-selected UUIDs to ProjectSummary objects so
  // the chips render with names. Cheap (one batch lookup); the names
  // rarely change.
  const idsKey = value.slice().sort().join(",");
  const idsQuery = useQuery<GetProjectsByIdsResponse>({
    queryKey: ["projects", "by-ids", idsKey],
    enabled: value.length > 0,
    queryFn: async () => {
      const response = await api.get<GetProjectsByIdsResponse>("/projects", {
        params: { ids: value.join(",") },
      });
      return response.data;
    },
    staleTime: 5 * 60_000,
  });

  // Merge: the selected projects (so chips have names) + the search
  // results (so the dropdown has options to pick from). De-dupe by id.
  const options = useMemo<ProjectSummary[]>(() => {
    const map = new Map<string, ProjectSummary>();
    for (const p of idsQuery.data?.items ?? []) map.set(p.id, p);
    for (const p of searchQuery.data?.items ?? []) map.set(p.id, p);
    return [...map.values()];
  }, [idsQuery.data?.items, searchQuery.data?.items]);

  const selectedObjects = useMemo<ProjectSummary[]>(() => {
    return value.map(
      (id): ProjectSummary =>
        idsQuery.data?.items.find((p) => p.id === id) ?? {
          id,
          name: id,
          code: "",
        },
    );
  }, [value, idsQuery.data?.items]);

  return (
    <Autocomplete<ProjectSummary, true>
      multiple
      disableCloseOnSelect
      size="small"
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
        // Clear the typed search string on close so reopening shows
        // the full top-50 again instead of the previous narrowed slice.
        // Mirrors the Status filter ergonomic (60_TEMPLATE_DATA_PAGE.md).
        setSearch("");
      }}
      options={options}
      value={selectedObjects}
      filterOptions={(x) => x} // server-side filter; don't double-filter client-side
      getOptionLabel={(o) => o.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      loading={searchQuery.isPending && open}
      onChange={(_e, next) => {
        onChange(next.map((p) => p.id));
        // Match Status filter: after picking, reset the typed query so
        // the dropdown immediately re-shows the full option list (the
        // popup stays open via `disableCloseOnSelect`).
        setSearch("");
      }}
      onInputChange={(_e, v, reason) => {
        if (reason === "input") setSearch(v);
      }}
      // Fixed width — Christian, 2026-05-13. Sized so a single typical
      // chip plus the MUI input-cursor min-width tile on ONE line; the
      // field grows in height only when 2+ chips force the second
      // chip to wrap. 220 px was too narrow because MUI Autocomplete
      // reserves ~80 px for the typing area, so even a single project
      // chip wrapped the cursor to a phantom second row. 280 px gives
      // ~180 px for the chip label, which fits the typical project
      // name. The Autocomplete's tag wrapper is `flex-wrap: wrap` by
      // default, so chips beyond the first tile to the next row.
      sx={{ width: 280 }}
      renderInput={(p) => {
        const { InputLabelProps, InputProps, inputProps, ...rest } = p;
        const tfProps = {
          ...rest,
          label: "Project",
          slotProps: {
            htmlInput: inputProps,
            input: {
              ...InputProps,
              endAdornment: (
                <>
                  {searchQuery.isPending && open ? (
                    <CircularProgress size={14} />
                  ) : null}
                  {InputProps.endAdornment}
                </>
              ),
            },
            inputLabel: { ...InputLabelProps, shrink: true },
          },
        } as unknown as TextFieldProps;
        return <TextField {...tfProps} />;
      }}
    />
  );
}
